<script setup>
import { useRoute } from 'vue-router'
import { useDark, useToggle, useStorage } from '@vueuse/core'
import { ref, inject, watch, computed } from 'vue'
import LogoImage from '@/assets/logo.png?w=56&h=56&format=webp&imagetools'
import { useAuth } from '@/composables/auth'
import api from '@/lib/api'

const darkMode = useDark({ selector: 'html' })
const route = useRoute()
const toggleDark = useToggle(darkMode)

const { sessionInfo, checkAuth } = useAuth()

const envOpts = ref([])
const currentEnvironment = useStorage('currentEnvironment', 'default')
const currentEnvironmentLabel = computed(() => envOpts.value.find(x => x.value === currentEnvironment.value)?.label)

api.settings.getEnvironments().then((res) => {
  if (!res.find(y => y.value === currentEnvironment.value)) currentEnvironment.value = 'default'
  envOpts.value = res
})
const sidebarAsMenu = ref()
const menu = ref()
const items = computed(() => [
  {
    label: sessionInfo.value.username,
    items: [
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: async () => {
          document.cookie = 'userdata=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
          checkAuth()
          await api.auth.logout()
        }
      }
    ]
  }
])

const toggle = (event) => {
  menu.value.toggle(event)
}

watch(route, () => sidebarAsMenu.value?.hide())

const sidebarCollapsed = inject('sidebarCollapsed')
const windowTooSmall = inject('windowTooSmall')
const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
}
</script>

<template>
  <div class="h-16 flex justify-between flex-row items-center gap-2 px-2 md:px-4 dark:bg-[#1f1f1f] bg-white border-b-[#e0e3e8] border-b dark:dark:border-b-gray-600">
    <div class="flex text-lg font-medium items-center gap-2 leading-5 min-w-0 flex-grow overflow-hidden">
      <span class="hidden md:inline">
        <Button
          aria-label="Collapse sidebar"
          icon="pi pi-bars"
          variant="text"
          rounded
          @click="toggleSidebar()"
        />
      </span>
      <div
        :style="{opacity: sidebarCollapsed || windowTooSmall ? 1 : 1}"
        class="flex items-center gap-2 transition-opacity duration-200 min-w-0"
      >
        <Avatar
          v-if="sidebarCollapsed || windowTooSmall"
          role="img"
          aria-label="SyncFDY Logo"
          class="flex-shrink-0"
          :image="LogoImage"
        />
        <Transition>
          <span
            v-if="sidebarCollapsed || windowTooSmall"
            class="hidden md:inline font-semibold text-2xl text-primary"
          >SyncFDY</span>
        </Transition>
        <div class="relative ml-5">
          <div class="diagonal-line" />
          <div
            class="absolute flex flex-row justify-center w-full"
            style="transform: rotate(-60deg); right: -17px; top: 19px; width: 86px; background: khaki"
          >
            <p class="font-bold text-orange-800">
              {{ currentEnvironmentLabel }}
            </p>
          </div>
        </div>
        <span
          v-if="!sidebarCollapsed && !windowTooSmall"
          class="font-semibold"
        >{{ typeof(route.meta.title) === 'function' ? route.meta.title(route) : route.meta.title }}</span>
        <button
          v-else
          aria-label="Show sidebar"
          class="cursor-pointer flex min-w-0 flex-grow whitespace-nowrap overflow-hidden items-center gap-2"
          @click="sidebarAsMenu.toggle($event)"
        >
          <span class="font-semibold text-ellipsis min-w-0 flex-grow inline-block overflow-hidden">
            {{ typeof(route.meta.title) === 'function' ? route.meta.title(route) : route.meta.title }}
          </span>
          <i class="pi pi-angle-down flex-shrink-0" />
          <Popover
            ref="sidebarAsMenu"
            class="max-h-[calc(100vh-4rem)] overflow-auto"
          >
            <HkSidebar embedded />
          </Popover>
        </button>
      </div>
    </div>
    <div class="flex items-center gap-2 flex-shrink-0">
      <Button
        :icon="darkMode ? 'pi pi-moon' : 'pi pi-sun'"
        text
        rounded
        class="flex-shrink-0"
        aria-label="Toggle dark mode"
        @click="toggleDark()"
      />
      <Button
        icon="pi pi-user"
        class="cursor-pointer flex-shrink-0 hover:outline hover:outline-[-p-button-primary-hover-color]"
        rounded
        @click="toggle"
      />
      <Menu
        id="overlay_menu"
        ref="menu"
        :model="items"
        :popup="true"
      />
    </div>
  </div>
</template>
<style scoped>
.diagonal-line {
  flex-shrink: 0;
  width: 32px;
  height: 55px;
  background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><line x1='0' y1='100' x2='100' y2='0' stroke='rgb(229, 231, 235)' vector-effect='non-scaling-stroke'/></svg>");
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 100% 100%, auto;
}

.dark .diagonal-line {
  filter: brightness(0) saturate(100%) invert(36%) sepia(6%) saturate(1215%) hue-rotate(176deg) brightness(89%) contrast(96%);
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  width: 0;
}
</style>
